import { Component, EventEmitter, Input, OnDestroy, OnInit, Output, Renderer2, ViewChild } from '@angular/core';
import { BehaviorSubject, forkJoin, iif, Observable, of, Subject, Subscription, throwError, zip } from 'rxjs';
import { catchError, filter, first, map, switchMap, take, takeUntil, tap } from 'rxjs/operators';
import { DynamicComponentDirective } from '../../../directives/dynamic-component.directive';
import * as Models from '../../../models/models-index';
import { ElementComponent } from '../../../models/models-index';
import { ReportsService } from '../../../services/api/reports.service';
import { PanelElementResolverService } from '../panel-element-resolver.service';
import * as SharedServices from '../../../services/services-index';
import { ChartService } from '../../chart/chart.service';
import { Store } from '@ngrx/store';
import { AppState } from '../../../../_store/app-state.model';
import { AppSelectors } from '../../../../_store/selector-types';

const clone = require('rfdc/default');

type PanelMetric = { name: string, displayName: string };
type SelectedConfiguration = { config: Models.PanelConfiguration, selectedMetrics: string[] };

@Component({
    selector: 'indexed-visualization-panel',
    templateUrl: './indexed-visualization-panel.component.html',
    styleUrls: ['./indexed-visualization-panel.component.scss'],
    standalone: false
})
export class IndexedVisualizationPanelComponent implements OnInit, OnDestroy, Models.PanelComponent {

  @ViewChild(DynamicComponentDirective, { static: true }) dynamicComponent!: DynamicComponentDirective;
  @Input() panelConfig: Models.Panel;
  private _dataSets: Models.DataSet[] = [];
  private componentInstance: ElementComponent;

  @Input()
  set dataSets(value: Models.DataSet[]) {
    this._dataSets = value;
    if(this._dataSets?.length > 0)
    {
      this.assignDataToElement();

      return;
    }

    if (this.componentInstance) {
      // set current element to loading state
      this.componentInstance.dataSets = null;

      // if filters change, make sure it changes element to the default one.
      // if(this.selectedConfiguration.config.elements[0].name != this.panelConfigs[0].elements[0].name) {
      //   this.renderDefaultElement();
      // }

      return;
    }
  }
  get dataSets(): Models.DataSet[] {
    return this._dataSets;
  }

  @Input() rowPanelId: string;
  @Input() translationService?: Models.ITranslationService;
  @Input() viewContext: Models.ViewContext;

  @Output() expandClicked = new EventEmitter<boolean>();

  panelTitle;
  headerConfig;
  subscriptions: Subscription[] = [];
  get panelConfigs() { return this.panelConfig.configurations }
  selectedConfiguration$ = new BehaviorSubject<SelectedConfiguration>(null);
  get selectedConfiguration() { return this.selectedConfiguration$.value; }

  panelMetrics$ = new BehaviorSubject<PanelMetric[]>([]);
  get panelMetrics() { return this.panelMetrics$.value; }
  selectedMetrics: string[] = [];
  locale: string;
  destroySubject = new Subject<void>();
  destroyed$ = this.destroySubject.asObservable();

  constructor(
    private store$: Store<AppState>,
    private resolverService: PanelElementResolverService,
    private chartService: ChartService,
    private renderer2: Renderer2,
    private reportService: ReportsService,
    private filterService: SharedServices.FilterService,
    private helpTextService: SharedServices.HelpTextService,
    private localeService: SharedServices.LocaleService) { }

  ngOnInit(): void {
    this.subscriptions.push(
      this.localeService.locale$.subscribe(loc => this.locale = loc),
      this.generateElements().subscribe(),
    );

    this.renderDefaultElement();
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach(s => s.unsubscribe());
    this.destroySubject.next();
    this.destroySubject.complete();
  }

  generateElements() {
    return this.selectedConfiguration$
      .pipe(
        filter(selectedConfiguration => !!selectedConfiguration?.config),
        map(selectedConfiguration => {
          const { config, selectedMetrics } = selectedConfiguration;
          const element = config.elements[0];
          this.panelTitle = config.label ?? config.name;

          this.selectedMetrics = selectedMetrics ?? [];

          const viewContainerRef = this.dynamicComponent.viewContainerRef;
          viewContainerRef.clear();

          const componentRef = this.resolverService.resolveElementComponent(viewContainerRef, element.type);
          this.componentInstance = componentRef.instance;
          this.componentInstance.settings = clone(element.settings);
          componentRef.instance.viewContext = this.viewContext;
          this.componentInstance.selectedMetric = this.selectedMetrics[0];
          this.componentInstance.selectedMetrics = this.selectedMetrics;
          componentRef.instance.panelConfiguration = config;
          this.renderer2.addClass(componentRef.location.nativeElement, 'indexed-visualization-panel-element');

          const dataSetsLoaded = this.dataSets && this.dataSets.length > 0;
          if (dataSetsLoaded) {
            this.assignDataToElement();
            return;
          }
        })
      )
  }

  configureMetrics(dataSets: Models.DataSet[]) {
    const dataSet = dataSets[0];
    if (!dataSet) {
      return;
    }
    const useElementMetrics = this.hasUseElementMetricsOption();
    const element = this.selectedConfiguration?.config?.elements?.[0];
    const configuredMetricNames = useElementMetrics ? element?.settings?.metrics
      ?.map(metric => metric?.name)
      .filter((name): name is string => !!name) : undefined;
    const columns = dataSet.columnSets.length > 0
      ? dataSet.columns.filter(c => dataSet.columnSets[0].columnNames.includes(c.name))
        .sort((a, b) => dataSet.columnSets[0].columnNames.indexOf(a.name) - dataSet.columnSets[0].columnNames.indexOf(b.name))
      : dataSet.columns;

    const eligibleColumns = configuredMetricNames?.length
      ? columns.filter(column => configuredMetricNames.includes(column.name))
      : columns;

    const metricsSource = configuredMetricNames?.length && eligibleColumns.length > 0
      ? eligibleColumns
      : columns;

    const metrics = metricsSource.map(c => ({ name: c.name, displayName: c.displayName }));

    this.panelMetrics$.next(metrics);
  }

  private hasUseElementMetricsOption(): boolean {
    const options = this.selectedConfiguration?.config?.options ?? [];
    return options.some(option => option?.name === 'useElementMetrics' && option.enabled);
  }

  loadDataSet(dataSetName: string): Observable<Models.DataSet> {
    // Check if the dataset with the given name exists in this.dataSets
    const existingDataSet = this.dataSets.find(
      (dataSet) => dataSet.name === dataSetName
    );

    // If the dataset exists, return it as an Observable
    if (existingDataSet) {
      return of(existingDataSet);
    }

    return this.store$.select(AppSelectors.selectCurrentRouteData).pipe(
      switchMap(routeData => this.getReportViewDataSet(routeData.reportName, dataSetName))
      );
  }


  loadDataSets(dataSetNames: string[]): Observable<Models.DataSet[]> {
    // Create an observable for each dataSet name
    const observables = dataSetNames.map(name => {
      // Check if the dataset exists in this.dataSets
      const existingDataSet = this.dataSets.find(dataSet => dataSet.name === name);
      if (existingDataSet) {
        // console.log('Returning existing dataset:', existingDataSet);
        return of(existingDataSet);
      }

      return this.getReportViewDataSet(this.viewContext.reportName, name).pipe(
        // tap(dataSet =>  console.log('Loaded dataset:', name, dataSet)),
        catchError((error) => {
          console.error(`Error loading dataset ${name}:`, error);

          return of(null);
        }
      ));
    });

    return forkJoin(observables);
  }

  getReportViewDataSet(reportName: string, dataSetName: string): Observable<Models.DataSet> {
    return this.filterService.getReportViewFilterRequestModel(reportName).pipe(
      take(1),
      map(requestModel => ({ ...requestModel, dataSets: [dataSetName] })),
      switchMap(requestModel => this.reportService.getDataSets(requestModel)),
      map((dataSets: Models.DataSet[]) => {
        if (!dataSets || dataSets.length == 0)
          throw new Error('No data sets returned from the API');

        this.dataSets.push(dataSets[0]);

        return dataSets[0];
      }),
      tap({
        error: err => {
          appInsights.trackException(err);
          // Rethrow the error to ensure it's propagated up the stream
          throw err;
        }
      }),
      catchError(err => {
        // Handle the error and return a safe value or an empty Observable
        // Example: return of({} as Models.DataSet);
        // Or rethrow the error if you want to handle it further up the chain
        return throwError(() => err);
      })
    );
  }

  indexChanged($event) {
    const changedPanelConfig = this.panelConfigs.find(c => c.name === $event.value.name);
    this.selectedConfiguration$.next({ config: changedPanelConfig, selectedMetrics: changedPanelConfig.elements[0].settings.defaultMetrics });
  }

  selectedMetricChanged($event, index: number) {
    const updatedMetrics = [...this.selectedMetrics];
    updatedMetrics[index] = $event.value;

    this.selectedConfiguration$.next({ config: this.selectedConfiguration.config, selectedMetrics: updatedMetrics });
  }

  expandButtonClicked(evt) {
    this.expandClicked.emit(evt)
  }

  openHelpTextClicked(): void {
    this.helpTextService.openHelp(this.selectedConfiguration.config.helpTextKey, this.selectedConfiguration.config.helpTextTitle);
  }

  toggleExpanded(evt) {
    const element = document.getElementById(this.rowPanelId);
    element.classList.toggle('wrap-row-panel');
    this.chartService.reflowCharts();
    // window.setTimeout(() => {
    //   element.scrollIntoView();
    // }, 300)
  }

  private assignDataToElement(): void {
    if (!this.componentInstance || !this.selectedConfiguration || !this.selectedConfiguration.config) {
      return;
    }

    const element = this.selectedConfiguration.config.elements[0];
    const dataSetNames = element.settings.dataSets ?? [element.settings.dataSet];
    const existingDataSets = this.dataSets
      .filter((ds) => dataSetNames.includes(ds.name))
      // keep the order of the datasets as they are in the element settings
      .sort((a, b) => dataSetNames.indexOf(a.name) - dataSetNames.indexOf(b.name));

    // validate that existing has all expected datasets otherwise we want to load them all
    if (existingDataSets.length === dataSetNames.length) {
      this.assignDataToGeneratedElement(existingDataSets, element)
      return;
    }

    console.log('loading datasets', dataSetNames);

    this.loadDataSets(dataSetNames).pipe(tap(dataSets => console.log('loaded datasets', dataSets))).subscribe(
      dataSets =>  {
        this.assignDataToGeneratedElement(dataSets, element);
      }
    );
  }

  assignDataToGeneratedElement(datasets: Models.DataSet[], element: Models.Element) {
    this.configureMetrics(datasets);
    if (!this.selectedConfiguration.config.disableMetricSelection && this.selectedMetrics.length === 0) {
      this.selectedMetrics.push(datasets[0].columns.find(c => c.type === 'metric')?.name);
      this.componentInstance.selectedMetric = this.selectedMetrics[0];
      this.componentInstance.selectedMetrics = this.selectedMetrics;
    }
    this.componentInstance.dataSets = datasets;
  }

  // isExpectedDataSet() {
  //   const element = this.selectedConfiguration.config.elements[0];
  //   const dataSetName = element.settings.dataSet;
  //   const isExpectedDataSet = !this.componentInstance.dataSet || this.componentInstance.dataSet?.name === dataSetName;

  //   return isExpectedDataSet;
  // }

  get filteredPanelConfigs(): Models.PanelConfiguration[] {
    const perspective = this.viewContext?.selectedPerspective;
    const panelSelector = perspective?.selectors.find(s => s.type === 'Panel' && s.path === this.panelConfig.name);
    // Start with all configurations
    let configs = this.panelConfig.configurations;
    // If items array is populated, filter and order by that list
    if (panelSelector?.items?.length) {
      configs = panelSelector.items
        .map(item => configs.find(c => c.name === item))
        .filter(cfg => !!cfg);
    }

    return configs;
  }

  renderDefaultElement(): void {
    const perspective = this.viewContext?.selectedPerspective;
    const panelSelector = perspective?.selectors.find(s => s.type === 'Panel' && s.path === this.panelConfig.name);
    const configs = this.filteredPanelConfigs;
    if (!configs?.length) return;

    const config = panelSelector?.defaultItem
      ? (configs.find(c => c.name === panelSelector.defaultItem) ?? configs[0])
      : configs[0];

    this.selectedConfiguration$.next({
      config,
      selectedMetrics: config.elements[0].settings.defaultMetrics
    });
  }
}
